/*
 |---------------------------------------------------------------------------
 | FilterForm
 |
 | AJAX filter form module met zoekveld, chips en paginatie, inclusief
 | scroll naar container rekening houdend met sticky header
 |---------------------------------------------------------------------------
 */

const FilterFormSelector = '#filterForm';

// --- 1. Class definitie ---
class FilterForm {
    constructor(form) {
        this.form = form;
        this.INPUT_SELECTOR = 'input[type="search"][name="searchTerm"]';;
        this.FILTER_SELECTORS = ['.a-chip-set a', '#filters-wissen-button']; // array van selectors
        this.PAGINATION_SELECTOR = '.a-pagination a';
        this.timeout = 1000;
        this.debounceTimer = null;
        this.lastScrollY = 0;
        this.previousSearchValue = "";
        this.previousCaretPos = 0;
        this.requestSeq = 0;
    }

    init() {
        if (!this.form) return;

        this.bindInput();
        this.bindFilters();
        this.bindSubmit();
        this.bindPagination();
        window.addEventListener('popstate', this.handlePopState);
    }

    log(...args) {
        try { console.log(...args); } catch (e) { }
    }

    buildUrl = () => {
        const formData = new FormData(this.form);
        return window.location.pathname + '?' + new URLSearchParams(formData).toString();
    };

    resetPageParam(url) {
        const u = new URL(url, window.location.origin);
        u.searchParams.delete("page");
        return u.toString();
    }

    doFetch = (url, opts = {}) => {
        const { push = false, scrollToFilters = false, restoreFocus = false } = opts;
        const seq = ++this.requestSeq;

        return fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
            .then(r => r.text())
            .then(html => {
                if (seq !== this.requestSeq) return;

                const parser = new DOMParser();
                const newDoc = parser.parseFromString(html, "text/html");
                const newContainer = newDoc.getElementById("filterform-container");
                const oldContainer = document.getElementById("filterform-container");

                if (!newContainer || !oldContainer) {
                    console.error("filterform-container niet gevonden");
                    return;
                }

                oldContainer.outerHTML = newContainer.outerHTML;

                // Herkoppel form na vervanging
                this.form = document.querySelector(FilterFormSelector);

                // Zoekveld herstellen
                const newInput = document.querySelector(this.INPUT_SELECTOR);
                if (newInput) {
                    newInput.value = this.previousSearchValue;

                    if (restoreFocus) {
                        newInput.focus();
                        newInput.setSelectionRange(this.previousCaretPos, this.previousCaretPos);
                    } else {
                        newInput.blur();
                    }
                }

                // Scrollgedrag
                if (scrollToFilters) {
                    const container = document.getElementById("filterform-container");
                    const header = document.querySelector('.site-header');
                    const headerHeight = header ? header.offsetHeight : 0;
                    const containerTop = container.getBoundingClientRect().top + window.scrollY;
                    const scrollToPosition = containerTop - headerHeight;

                    window.scrollTo({ top: scrollToPosition, behavior: 'smooth' });
                } else {
                    window.scrollTo({ top: this.lastScrollY, behavior: 'auto' });
                }

                // History bijwerken
                if (push) {
                    history.pushState({ url: url }, "", url);
                }
            })
            .catch(err => console.error("Fetch fout:", err));
    };

    handlePopState = (e) => {
        const url = (e.state && e.state.url) || window.location.href;
        this.doFetch(url, { push: false, scrollToFilters: true, restoreFocus: false });
    };

    bindInput() {
        document.addEventListener('input', (e) => {
            const target = e.target.closest(this.INPUT_SELECTOR);
            if (!target || !this.form.contains(target)) return;

            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                const input = this.form.querySelector(this.INPUT_SELECTOR);
                if (!input) return;

                const value = input.value.trim();

                // Alleen zoeken vanaf 3+ karakters, maar bij leeg veld altijd zoeken
                if (value.length > 0 && value.length < 3) {
                    return; // te kort, niets doen
                }

                this.lastScrollY = window.scrollY;
                this.previousSearchValue = value;
                this.previousCaretPos = input.selectionStart || value.length;

                const url = this.resetPageParam(this.buildUrl());
                this.doFetch(url, { push: true, scrollToFilters: false, restoreFocus: true });
            }, this.timeout);
        });

        document.addEventListener('keydown', (e) => {
            const target = e.target.closest(this.INPUT_SELECTOR);
            if (target && this.form.contains(target) && e.key === 'Enter') {
                e.preventDefault();
            }
        });
    }

    bindFilters() {
        document.addEventListener('click', (e) => {
            let link = null;
            for (const sel of this.FILTER_SELECTORS) {
                link = e.target.closest(sel);
                if (link) break;
            }
            if (!link) return;
            e.preventDefault();

            this.lastScrollY = window.scrollY;
            const input = this.form.querySelector(this.INPUT_SELECTOR);
            if (input) {
                this.previousSearchValue = input.value;
                this.previousCaretPos = input.selectionStart || input.value.length;
            }
            const url = this.resetPageParam(link.href);
            this.doFetch(url, { push: true, scrollToFilters: false, restoreFocus: false });
        });
    }

    bindSubmit() {
        this.form.addEventListener("submit", (e) => {
            e.preventDefault();

            this.lastScrollY = window.scrollY;
            const input = this.form.querySelector(this.INPUT_SELECTOR);
            if (input) {
                this.previousSearchValue = input.value;
                this.previousCaretPos = input.selectionStart || input.value.length;
            }

            const url = this.resetPageParam(this.buildUrl());
            this.doFetch(url, { push: true, scrollToFilters: false, restoreFocus: false });
        });
    }

    bindPagination() {
        document.addEventListener('click', (e) => {
            const link = e.target.closest(this.PAGINATION_SELECTOR);
            if (!link) return;
            e.preventDefault();

            const input = this.form.querySelector(this.INPUT_SELECTOR);
            if (input) {
                this.previousSearchValue = input.value;
                this.previousCaretPos = input.selectionStart || input.value.length;
            }
            this.doFetch(link.href, { push: true, scrollToFilters: true, restoreFocus: false });
        });
    }
}

// --- 2. Helper functie ---
function FilterFormCreateInstance(element) {
    if (!element.filterFormInstance) {
        element.filterFormInstance = new FilterForm(element);
        element.filterFormInstance.init();
    }
}

// --- 3. Bestaande elementen initialiseren ---
document.querySelectorAll(FilterFormSelector).forEach(FilterFormCreateInstance);

// --- 4. DOM observer starten ---
const FilterFormObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
            if (node.matches && node.matches(FilterFormSelector)) {
                FilterFormCreateInstance(node);
            }
        });
    });
});

FilterFormObserver.observe(document, { childList: true, subtree: true });
